import React, { useMemo } from "react";
import { Table, InputGroup, FormControl } from "react-bootstrap";
import { useTable, useFilters, useGlobalFilter, useSortBy } from "react-table";
import tw from "twin.macro";

const portfolioPositionsData = [
  {
    segment: "Equity",
    productType: "Cash",
    exchangeCode: "NSE",
    stockCode: "STK123",
    expiryDate: "2024-06-30",
    strikePrice: "$55",
    right: "Yes",
    action: "Buy",
    quantity: "100",
    averagePrice: "$50",
    settlementId: "SET123",
    marginAmount: "$5000",
    LTP: "$55",
    price: "$52",
    stockIndexIndicator: "NIFTY",
    coverQuantity: "20",
    stoplossTrigger: "$48",
    stoploss: "$45",
    takeProfit: "$60",
    availableMargin: "$1500",
    squareoffMode: "Auto",
    MTFSellQuantity: "50",
    MTFNetAmountPayable: "$2000",
    MTFExpiryDate: "2024-07-30",
    orderId: "ORD123",
    coverOrderFlow: "Sequential",
    coverOrderExecutedQuantity: "30",
    pledgeStatus: "Pledged",
    PNL: "$300",
    underlying: "Underlying Stock",
    orderSegmentCode: "ORD_SEG123",
  },
  {
    segment: "Derivative",
    productType: "Future",
    exchangeCode: "NFO",
    stockCode: "STK124",
    expiryDate: "2024-07-15",
    strikePrice: "$50",
    right: "No",
    action: "Sell",
    quantity: "200",
    averagePrice: "$45",
    settlementId: "SET124",
    marginAmount: "$9000",
    LTP: "$47",
    price: "$46",
    stockIndexIndicator: "BANKNIFTY",
    coverQuantity: "40",
    stoplossTrigger: "$43",
    stoploss: "$42",
    takeProfit: "$55",
    availableMargin: "$2500",
    squareoffMode: "Manual",
    MTFSellQuantity: "80",
    MTFNetAmountPayable: "$3500",
    MTFExpiryDate: "2024-08-30",
    orderId: "ORD124",
    coverOrderFlow: "Parallel",
    coverOrderExecutedQuantity: "60",
    pledgeStatus: "Not Pledged",
    PNL: "$400",
    underlying: "Underlying Index",
    orderSegmentCode: "ORD_SEG124",
  },
  // Add more portfolio positions data as needed
];

const Container = tw.div`bg-white px-4 pt-3 pb-4 rounded-sm border border-gray-200 flex-1`;
const Title = tw.h1`text-xl font-bold text-blue-600 mb-2 text-center`;
const TableWrapper = tw.div`border-x border-gray-200 rounded-sm mt-3 overflow-auto`;

export default function PortfolioPositions() {
  const data = useMemo(() => portfolioPositionsData, []);

  const columns = useMemo(
    () => [
      { Header: "Segment", accessor: "segment" },
      { Header: "Product Type", accessor: "productType" },
      { Header: "Exchange Code", accessor: "exchangeCode" },
      { Header: "Stock Code", accessor: "stockCode" },
      { Header: "Expiry Date", accessor: "expiryDate" },
      { Header: "Strike Price", accessor: "strikePrice" },
      { Header: "Right", accessor: "right" },
      { Header: "Action", accessor: "action" },
      { Header: "Quantity", accessor: "quantity" },
      { Header: "Average Price", accessor: "averagePrice" },
      { Header: "Settlement ID", accessor: "settlementId" },
      { Header: "Margin Amount", accessor: "marginAmount" },
      { Header: "LTP", accessor: "LTP" },
      { Header: "Price", accessor: "price" },
      { Header: "Stock Index Indicator", accessor: "stockIndexIndicator" },
      { Header: "Cover Quantity", accessor: "coverQuantity" },
      { Header: "Stoploss Trigger", accessor: "stoplossTrigger" },
      { Header: "Stoploss", accessor: "stoploss" },
      { Header: "Take Profit", accessor: "takeProfit" },
      { Header: "Available Margin", accessor: "availableMargin" },
      { Header: "Squareoff Mode", accessor: "squareoffMode" },
      { Header: "MTF Sell Quantity", accessor: "MTFSellQuantity" },
      { Header: "MTF Net Amount Payable", accessor: "MTFNetAmountPayable" },
      { Header: "MTF Expiry Date", accessor: "MTFExpiryDate" },
      { Header: "Order ID", accessor: "orderId" },
      { Header: "Cover Order Flow", accessor: "coverOrderFlow" },
      { Header: "Cover Order Executed Quantity", accessor: "coverOrderExecutedQuantity" },
      { Header: "Pledge Status", accessor: "pledgeStatus" },
      { Header: "PNL", accessor: "PNL" },
      { Header: "Underlying", accessor: "underlying" },
      { Header: "Order Segment Code", accessor: "orderSegmentCode" },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
    state,
  } = useTable(
    {
      columns,
      data,
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  const { globalFilter } = state;

  return (
    <Container>
      <Title>Portfolio Positions</Title>
      <InputGroup className="my-3">
        <FormControl
          value={globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
        />
      </InputGroup>
      <TableWrapper>
        <Table striped bordered hover responsive {...getTableProps()} style={{ fontSize: '12px' }}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()} style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div>{column.render('Header')}</div>
                      {column.canSort && (
                        <div {...column.getSortByToggleProps()} style={{ cursor: 'pointer' }}>
                          {column.isSorted ? (
                            column.isSortedDesc ? '🔽' : '🔼'
                          ) : (
                            <>
                              <span>🔼</span>
                              <span>🔽</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrapper>
    </Container>
  );
}
